import React, { useEffect, useRef } from 'react';
import './MouseAnimation.css';

const MouseAnimation = ({ isDarkMode }) => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0, vx: 0, vy: 0 });
  const particlesRef = useRef([]);
  const animationRef = useRef(null);
  const lastMouseRef = useRef({ x: 0, y: 0 });
  const isTouchDevice = useRef('ontouchstart' in window || navigator.maxTouchPoints > 0);

  useEffect(() => {
    // If it's a touch device, don't initialize the animation
    if (isTouchDevice.current) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (isTouchDevice.current) {
      return;
    }

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Enhanced mouse move handler with velocity tracking
    const handleMouseMove = (e) => {
      const currentX = e.clientX;
      const currentY = e.clientY;

      // Calculate mouse velocity
      mouseRef.current.vx = currentX - lastMouseRef.current.x;
      mouseRef.current.vy = currentY - lastMouseRef.current.y;

      mouseRef.current.x = currentX;
      mouseRef.current.y = currentY;

      lastMouseRef.current.x = currentX;
      lastMouseRef.current.y = currentY;

      // Calculate mouse speed for dynamic effects
      const speed = Math.sqrt(mouseRef.current.vx ** 2 + mouseRef.current.vy ** 2);
      const maxSpeed = 50;
      const normalizedSpeed = Math.min(speed / maxSpeed, 1);

      // Add particles based on speed (reduced to prevent accumulation)
      const particleCount = Math.floor(normalizedSpeed * 1.5) + 1;

      // Limit total particles to prevent performance issues
      if (particlesRef.current.length > 100) {
        particlesRef.current = particlesRef.current.slice(-50);
      }

      for (let i = 0; i < particleCount; i++) {
        // Create different types of particles
        const particleType = Math.random();

        if (particleType < 0.6) {
          // Standard particles
          particlesRef.current.push({
            x: currentX,
            y: currentY,
            vx: (Math.random() - 0.5) * 2 + mouseRef.current.vx * 0.1,
            vy: (Math.random() - 0.5) * 2 + mouseRef.current.vy * 0.1,
            life: 1,
            decay: 0.03,
            size: Math.random() * 3 + 1,
            type: 'standard',
            color: isDarkMode ?
              `hsl(${Math.random() * 60 + 180}, 70%, 60%)` :
              `hsl(${Math.random() * 60 + 200}, 70%, 50%)`
          });
        } else if (particleType < 0.8) {
          // Spark particles
          particlesRef.current.push({
            x: currentX,
            y: currentY,
            vx: (Math.random() - 0.5) * 4,
            vy: (Math.random() - 0.5) * 4,
            life: 0.8,
            decay: 0.04,
            size: Math.random() * 2 + 0.5,
            type: 'spark',
            color: isDarkMode ?
              `hsl(${Math.random() * 30 + 45}, 90%, 70%)` :
              `hsl(${Math.random() * 30 + 15}, 90%, 60%)`
          });
        } else {
          // Magnetic particles
          particlesRef.current.push({
            x: currentX,
            y: currentY,
            vx: 0,
            vy: 0,
            life: 1.5,
            decay: 0.01,
            size: Math.random() * 4 + 2,
            type: 'magnetic',
            color: isDarkMode ?
              `hsl(${Math.random() * 40 + 160}, 80%, 65%)` :
              `hsl(${Math.random() * 40 + 180}, 80%, 55%)`
          });
        }
      }
    };

    // Animation loop with enhanced particle physics
    const animate = () => {
      // Clear the entire canvas more thoroughly
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'transparent';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles with enhanced physics
      particlesRef.current = particlesRef.current.filter(particle => {
        // Apply magnetic attraction to mouse for magnetic particles
        if (particle.type === 'magnetic') {
          const dx = mouseRef.current.x - particle.x;
          const dy = mouseRef.current.y - particle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance > 0) {
            particle.vx += (dx / distance) * 0.5;
            particle.vy += (dy / distance) * 0.5;
          }
        }

        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Apply friction
        particle.vx *= 0.98;
        particle.vy *= 0.98;

        particle.life -= particle.decay;
        particle.size *= 0.99;

        if (particle.life > 0) {
          ctx.save();
          ctx.globalAlpha = particle.life;

          if (particle.type === 'spark') {
            // Draw spark effect
            ctx.strokeStyle = particle.color;
            ctx.lineWidth = particle.size;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(particle.x - particle.vx * 2, particle.y - particle.vy * 2);
            ctx.stroke();
          } else if (particle.type === 'magnetic') {
            // Draw magnetic particle with glow
            ctx.shadowColor = particle.color;
            ctx.shadowBlur = 10;
            ctx.fillStyle = particle.color;
            ctx.beginPath();
            ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
            ctx.fill();
          } else {
            // Draw standard particle
            ctx.fillStyle = particle.color;
            ctx.beginPath();
            ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
            ctx.fill();
          }

          ctx.restore();
          return true;
        }
        return false;
      });

      // Draw enhanced mouse cursor trail (shorter and less persistent)
      if (mouseRef.current.x > 0 && mouseRef.current.y > 0) {
        const speed = Math.sqrt(mouseRef.current.vx ** 2 + mouseRef.current.vy ** 2);

        // Only draw trail if speed is reasonable (not too fast)
        if (speed < 30) {
          ctx.save();
          ctx.strokeStyle = isDarkMode ?
            `rgba(80, 227, 194, ${0.2 + speed * 0.005})` :
            `rgba(50, 145, 255, ${0.2 + speed * 0.005})`;
          ctx.lineWidth = 1 + speed * 0.03;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(mouseRef.current.x, mouseRef.current.y);
          ctx.lineTo(
            mouseRef.current.x - mouseRef.current.vx * 0.3,
            mouseRef.current.y - mouseRef.current.vy * 0.3
          );
          ctx.stroke();

          // Draw cursor glow
          ctx.shadowColor = isDarkMode ? 'rgba(80, 227, 194, 0.5)' : 'rgba(50, 145, 255, 0.5)';
          ctx.shadowBlur = 15;
          ctx.fillStyle = isDarkMode ? 'rgba(80, 227, 194, 0.3)' : 'rgba(50, 145, 255, 0.3)';
          ctx.beginPath();
          ctx.arc(mouseRef.current.x, mouseRef.current.y, 3, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', handleMouseMove);
    animate();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isDarkMode]);

  // Don't render the canvas on touch devices
  if (isTouchDevice.current) {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      className="mouse-animation-canvas"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1000,
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
      }}
    />
  );
};

export default MouseAnimation;
